/**
 * Llan-y-Nant, as a rule set (M4-08's framework, M5-01's village).
 *
 * Two things live here and nothing else: **the day** — walking everybody who
 * lives here to wherever their schedule says they should be — and **the
 * conversation**, one intent with a topic, dispatched to whichever service was
 * asked for. The services themselves are elsewhere: the Meddyg's are
 * `meddyg.ts`, the Smith's are `barter.ts`, and the Elder's news is
 * `chronicle.ts`. This file is the seam, and it is deliberately thin enough
 * that adding the sixth villager is a content change.
 *
 * The claim M4-08 made when it built a framework for one NPC was that the smith
 * would arrive by adding data. He does: `SMITH` is a `VillagerDef` with a stall
 * on it, and the only code below that knows a smith exists is the `case 'buy'`
 * that reaches for `def.stall` — which the Meddyg simply does not have.
 *
 * What the village is *for* is the hunt. The elder tells you what the world sim
 * actually did last night, the smith turns what you killed into what you kill
 * the next thing with, and the herders are standing next to the animals the
 * wolves are coming for. Errands now pass through this seam too, but only as
 * data observed from the same world events: dialogue never invents a parallel
 * version of gathering, firemaking, medicine or combat.
 */
import { chebyshev } from './actor.js';
import { parseTalk } from './actions.js';
import { buyFault, giveWare, mend, priceOf, readStall, repairFault, wareName, wareOn, } from './barter.js';
import { latestNews, renderRumour } from './chronicle.js';
import { penCensus } from './raids.js';
import { isHelpless } from './fighter.js';
import { nextErrandFrom, offerErrand, renderErrandPrompt } from './errands.js';
import { treat } from './meddyg.js';
import { canStand } from './movement.js';
import { gameTime } from './time.js';
import { stopFor } from './villagers.js';
import { line, say, TALK_RANGE } from './voice.js';
/* -------------------------------------------------------------------------- *
 * The day
 * -------------------------------------------------------------------------- */
/** How often a villager takes a step. Walking pace, not a player's. */
export const VILLAGER_STEP_TICKS = 4;
/**
 * Walk the villagers through their day.
 *
 * A step at a time and only when they are somewhere they should not be, so a
 * villager crosses the green rather than teleporting across it — the schedule
 * is a thing players should be able to *watch* happen, and somebody who blinked
 * between the well and her door would read as scenery rather than as a person.
 *
 * No pathfinding: one greedy step, refused into walls. The village is open
 * ground and the stops are a dozen tiles apart; a stuck villager finds her way
 * on the next hour's stop, and a valley that needed A* to get its healer home
 * would be a valley with a level design problem.
 *
 * A stop that is not standable counts as reached from beside it. Half the
 * places a person's day is actually spent are impassable furniture — a hearth,
 * a well, a forge — and without this the healer spends every tick of every
 * evening walking into the well she is drawing from.
 */
export const villagerSystem = {
    name: 'villagers',
    phase: 'sim',
    run(ctx) {
        if (ctx.tick % VILLAGER_STEP_TICKS !== 0)
            return;
        const hour = gameTime(ctx.tick).hour;
        for (const id of Object.keys(ctx.world.villagers ?? {}).sort()) {
            const villager = ctx.world.villagers[id];
            const actor = ctx.world.actors[id];
            const combatant = ctx.world.combat[id];
            if (!villager || !actor || !combatant)
                continue;
            // The dead and the senseless keep no appointments.
            if (isHelpless(ctx.catalogue, combatant, ctx.tick))
                continue;
            const want = stopFor(ctx.catalogue, villager, hour);
            if (!want)
                continue;
            if (arrived(ctx.world, actor, want))
                continue;
            const dx = Math.sign(want.x - actor.x);
            const dy = Math.sign(want.y - actor.y);
            for (const [stepX, stepY] of [
                [dx, dy],
                [dx, 0],
                [0, dy],
            ]) {
                if (stepX === 0 && stepY === 0)
                    continue;
                const x = actor.x + stepX;
                const y = actor.y + stepY;
                if (!canStand(ctx.world, x, y, actor.id))
                    continue;
                actor.x = x;
                actor.y = y;
                break;
            }
        }
    },
};
/** Standing on the stop, or beside one nobody could stand on. */
function arrived(world, actor, want) {
    if (actor.x === want.x && actor.y === want.y)
        return true;
    if (canStand(world, want.x, want.y, actor.id))
        return false;
    return chebyshev(actor.x, actor.y, want.x, want.y) <= 1;
}
/* -------------------------------------------------------------------------- *
 * Talking to them
 * -------------------------------------------------------------------------- */
/** Why you cannot talk to them, or `null`. */
export function talkFault(world, catalogue, actor, targetId, tick) {
    const target = world.actors[targetId];
    if (!target || !world.villagers[targetId])
        return 'there is nobody there to talk to';
    if (chebyshev(actor.x, actor.y, target.x, target.y) > TALK_RANGE) {
        return `${target.name} is too far off to hear you`;
    }
    const body = world.combat[targetId];
    if (body?.vitals.dead)
        return `${target.name} is dead`;
    if (body && isHelpless(catalogue, body, tick))
        return `${target.name} cannot answer`;
    const speaker = world.combat[actor.id];
    if (speaker && isHelpless(catalogue, speaker, tick))
        return 'you are in no state to';
    return null;
}
/** Do they answer this topic at all? Content's list, not a hard-coded one. */
export function offers(def, topic) {
    return def.topics.includes(topic);
}
/**
 * The Elder's line, or `null` when the valley has genuinely been quiet.
 *
 * Rendered from the chronicle every time it is asked rather than cached, so it
 * updates by the game-day without anybody having to invalidate anything: the
 * news *is* the tuple, and the sentence is a view of it.
 */
export function newsFor(world, catalogue, def, tick) {
    const pools = catalogue.rumours?.() ?? null;
    if (!pools)
        return null;
    // Which news this villager carries is content's call (`VillagerDef.news`):
    // the Elder takes the lot because that is his job, and the herders answer
    // about the pen because that is what they were awake for. What nobody has is
    // a way to *invent* news — an empty slice falls through to the ambient pool
    // and the villager talks about the weather instead.
    const happening = latestNews(world.chronicle ?? [], tick, def.news);
    if (!happening)
        return null;
    return renderRumour(pools, happening, tick);
}
/**
 * Refill a wholly spent ranged weapon while its supporting errand is active.
 *
 * The errand definition owns who, what and how many. Dialogue owns only the
 * invariant the player can state: no arrows, unfinished work, speak to the
 * named contact. Looking exclusively in `active` makes completion the cutoff
 * without a quest id or story branch leaking into the village rules.
 */
function resupplyActiveErrand(ctx, actor, villagerActor, villagerDef) {
    const combatant = ctx.world.combat[actor.id];
    if (!combatant || (combatant.loadout.ammunition ?? 0) !== 0)
        return false;
    for (const active of ctx.world.errands[actor.id]?.active ?? []) {
        const errand = ctx.catalogue.errand?.(active.defId) ?? null;
        const supply = errand?.resupply;
        if (!supply || supply.villager !== villagerDef.id)
            continue;
        if (!ctx.catalogue.weapon(supply.weaponId)?.ranged)
            continue;
        if (!Number.isInteger(supply.ammunition) || supply.ammunition <= 0)
            continue;
        // Ammunition stays with the loadout while a physical weapon is stowed. The
        // active errand proves its earlier handoff, so refill the quiver before the
        // player takes the bow back out rather than requiring an empty weapon in hand.
        combatant.loadout = { ...combatant.loadout, ammunition: supply.ammunition };
        say(ctx, villagerActor, actor, supply.text, `errand:${errand.id}:resupply`);
        return true;
    }
    return false;
}
/* -------------------------------------------------------------------------- *
 * The handler
 * -------------------------------------------------------------------------- */
export const talkHandler = {
    type: 'talk',
    parse: parseTalk,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return `${intent.actorId} is not in the world`;
        return talkFault(ctx.world, ctx.catalogue, actor, intent.payload.targetId, ctx.tick);
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const villagerActor = ctx.world.actors[intent.payload.targetId];
        const villager = ctx.world.villagers[intent.payload.targetId];
        if (!actor || !villagerActor || !villager)
            return;
        const def = ctx.catalogue.villager?.(villager.defId);
        if (!def)
            return;
        const topic = (intent.payload.topic ?? 'greet');
        // Capture this before emitting the talk event: errand tracking consumes
        // that event immediately and removes the completed handoff from `active`.
        const handoffReward = topic === 'greet'
            ? (ctx.world.errands[actor.id]?.active
                .map((one) => ctx.catalogue.errand?.(one.defId) ?? null)
                .find((errand) => errand?.trigger?.kind === 'talk-to' &&
                errand.trigger.villager === def.id &&
                errand.reward?.text) ?? null)
            : null;
        // A conversation is itself a world event. This is the reusable closer for
        // “find and speak to somebody” objectives; the villager definition id is
        // carried explicitly so quest data never has to guess an actor-instance id.
        ctx.emit({
            type: 'interacted',
            actors: [actor.id, villagerActor.id],
            refs: ['talk', 'done', def.id, topic],
            audience: 'archive',
            at: { x: actor.x, y: actor.y },
        });
        // Asked for something they do not do. Refused *here* rather than in
        // `check`, and out loud rather than as a rejection: "the herder cannot
        // help you with that" is a thing a person says, and a silent rejection
        // would read to a player as the key being broken.
        if (topic !== 'greet' && !offers(def, topic)) {
            if (topic === 'errand') {
                const held = ctx.world.errands[actor.id]?.active.find((one) => one.defId === intent.payload.subject);
                const heldDef = held ? (ctx.catalogue.errand?.(held.defId) ?? null) : null;
                if (heldDef?.giver === def.id && heldDef.dialogue) {
                    say(ctx, villagerActor, actor, renderErrandPrompt(ctx.world, ctx.catalogue, heldDef, actor), `errand:${heldDef.id}:dialogue`);
                    return;
                }
            }
            say(ctx, villagerActor, actor, line(def.lines.greet, ctx.tick), 'cannot-help');
            return;
        }
        if (topic === 'greet') {
            // A cross-villager handoff closes after this intent is processed. Keep
            // its physical reward in the conversation itself instead of letting a
            // generic greeting cover the popup while the meaningful line appears
            // behind it in the chronicle (the Nest → Deri bow handoff).
            if (handoffReward?.reward?.text) {
                say(ctx, villagerActor, actor, handoffReward.reward.text, `errand:${handoffReward.id}:reward`);
                return;
            }
            if (resupplyActiveErrand(ctx, actor, villagerActor, def))
                return;
            const held = ctx.world.errands[actor.id]?.active.find((one) => {
                const errand = ctx.catalogue.errand?.(one.defId) ?? null;
                return errand?.giver === def.id;
            });
            const heldDef = held ? (ctx.catalogue.errand?.(held.defId) ?? null) : null;
            if (heldDef) {
                // A contextual response owns the meaningful next beat. Greeting stays
                // a greeting; the player then chooses the authored errand line instead
                // of pressing "greet" until something progresses.
                if (heldDef.dialogue) {
                    say(ctx, villagerActor, actor, line(def.lines.greet, ctx.tick), 'greet');
                    return;
                }
                say(ctx, villagerActor, actor, renderErrandPrompt(ctx.world, ctx.catalogue, heldDef, actor), `errand:${heldDef.id}:reminder`);
                return;
            }
            // Content order is story order. Availability is still rules data, so the
            // same mechanism can offer an unrelated open-world errand months later.
            const next = nextErrandFrom(ctx.world, ctx.catalogue, actor.id, def.id, ctx.tick);
            if (next) {
                const offered = offerErrand(ctx.world, actor.id, next, ctx.tick);
                if (offered)
                    ctx.emit(offered);
                if (next.dialogue) {
                    say(ctx, villagerActor, actor, line(def.lines.greet, ctx.tick), 'greet');
                    return;
                }
                say(ctx, villagerActor, actor, renderErrandPrompt(ctx.world, ctx.catalogue, next, actor), `errand:${next.id}:offered`);
                return;
            }
        }
        switch (topic) {
            case 'treat': {
                const patient = ctx.world.combat[actor.id];
                if (patient)
                    treat(ctx, actor, patient, villagerActor, def);
                return;
            }
            case 'bed':
                say(ctx, villagerActor, actor, line(def.lines.bed, ctx.tick), 'bed');
                return;
            case 'rumour':
                say(ctx, villagerActor, actor, newsFor(ctx.world, ctx.catalogue, def, ctx.tick) ?? line(def.lines.rumour, ctx.tick), 'rumour');
                return;
            case 'trade':
                say(ctx, villagerActor, actor, line(def.lines.wares, ctx.tick), 'wares');
                // Priced against the fold as it stands this morning (M5-02). A village
                // the wolves have been at is a village with less to trade away, and the
                // smith is the first person a player hears it from.
                say(ctx, villagerActor, actor, readStall(ctx.catalogue, def, herdSize(ctx)), 'stall');
                return;
            case 'buy':
                buy(ctx, actor, villagerActor, def, intent.payload.subject ?? '');
                return;
            case 'repair':
                repair(ctx, actor, villagerActor, def);
                return;
            default:
                say(ctx, villagerActor, actor, line(def.lines.greet, ctx.tick), 'greet');
        }
    },
};
/**
 * How many animals the village still has, for the prices to be quoted against
 * (M5-02).
 *
 * Counted here rather than cached anywhere, because the census *is* the count of
 * the actors: there is no second number that could disagree with what is
 * standing in the fold, which is what makes "pen losses propagate" true rather
 * than something somebody has to remember to propagate.
 */
function herdSize(ctx) {
    return penCensus(ctx.world, ctx.catalogue).length;
}
/** Buying one named thing off a stall. */
function buy(ctx, actor, seller, def, wareId) {
    const combatant = ctx.world.combat[actor.id];
    const inventory = ctx.world.inventories[actor.id];
    const herd = herdSize(ctx);
    const fault = buyFault(ctx.catalogue, def, combatant, inventory, wareId, herd);
    if (fault !== null) {
        // `hands-full` shares the refusal pool with `no-sale`: both are the smith
        // declining, and the `refs` on the event carry which one it was for anyone
        // reading the archive.
        const pool = fault === 'cannot-afford' ? def.lines.cannotAfford : def.lines.noSale;
        say(ctx, seller, actor, line(pool, ctx.tick), fault);
        return;
    }
    const ware = wareOn(def.stall, wareId);
    if (!ware || !giveWare(ctx.catalogue, combatant, inventory, ware, ctx.tick, herd))
        return;
    say(ctx, seller, actor, line(def.lines.traded, ctx.tick), 'traded');
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, seller.id],
        // What was actually paid, not what the list price says — the archive should
        // record the bad winter as well as the trade.
        refs: ['trade', 'bought', ware.id, String(priceOf(ware, herd))],
        text: `${actor.name} trades for ${wareName(ctx.catalogue, ware)}.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/** Putting an edge back on what the asker is holding. */
function repair(ctx, actor, smith, def) {
    const combatant = ctx.world.combat[actor.id];
    const inventory = ctx.world.inventories[actor.id];
    const fault = repairFault(ctx.catalogue, def, combatant, inventory);
    if (fault !== null) {
        const pool = fault === 'cannot-afford' ? def.lines.cannotAfford : def.lines.nothingToMend;
        say(ctx, smith, actor, line(pool, ctx.tick), fault);
        return;
    }
    if (!combatant || !mend(ctx.catalogue, def, combatant, inventory))
        return;
    say(ctx, smith, actor, line(def.lines.repaired, ctx.tick), 'repaired');
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, smith.id],
        refs: ['trade', 'repaired', combatant.loadout.weaponId],
        text: `${smith.name} works ${actor.name}'s edge back onto it.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
export const VILLAGER_INTENTS = [talkHandler];
//# sourceMappingURL=village.js.map